import React, {FC, useEffect, useReducer, useState, useRef} from 'react'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import ProductGeneralForm from './general/ProductGeneralForm'
import {fieldReducer, IProductProps, productActionType, productInitialState} from '../cors/reducers'
import '../../../stylesheet/ProductForm.css'
import {useIntl} from "react-intl";
import {addProduct, saveProductDraft} from "../cors/_requests";
import SharedData from "../../common/SharedData";
import {useNavigate, useParams} from "react-router-dom";
import AlertMessage from "../../common/alert/AlertMsg";
import SpecialPriceDecisionModal from './components/SpecialPriceDecisionModal';
import NewSpecialPriceModal from './components/NewSpecialPriceModal';
import PackageSection from './PackageSection';
import {isShopUser} from '../../common/stockScope';
import {tenantTaxEnabled} from '../../../tenant/tenantState';

class MissingFieldsError extends Error {
    constructor(public details: string[]) {
        super('missing_fields');
        this.name = 'MissingFieldsError';
    }
}

class InvalidFieldValuesError extends Error {
    constructor(public details: string[]) {
        super('invalid_values');
        this.name = 'InvalidFieldValuesError';
    }
}

class VariationValidationError extends Error {
    constructor(public details: string[]) {
        super('variation_validation');
        this.name = 'VariationValidationError';
    }
}

class FormValidationError extends Error {
    constructor(public details: string[]) {
        super('form_validation');
        this.name = 'FormValidationError';
    }
}

interface ValidationResult {
    missingFields: string[];
    invalidValues: string[];
    variationIssues: string[];
}

const ProductGeneralTab: FC<IProductProps> = ({ prodState, prodDispatch }) => {

    const { formatMessage } = useIntl();
    const [state, dispatch] = useReducer(fieldReducer, productInitialState)
    const {currentUser, language} = SharedData()
    const {productID} = useParams()
    const [currentStep, setCurrentStep] = useState( productID ? 2 : 0);
    const navigate = useNavigate();
    const [key, setKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [errorMsg, setErrorMsg] = useState({
        title: '',
        message: '',
    })
    const [originalPrice, setOriginalPrice] = useState<string>('')
    const [showSpecialPriceModal, setShowSpecialPriceModal] = useState(false)
    const [showNewSpecialPriceModal, setShowNewSpecialPriceModal] = useState(false)
    const [calculatedNewSpecialPrice, setCalculatedNewSpecialPrice] = useState<string>('0')
    const [specialPriceDecision, setSpecialPriceDecision] = useState<'deactivate' | 'keep_active' | null>(null)
    const originalPriceRef = useRef<string>('')
    const steps = [
        'barcode',
        'Product Category',
        'English Product Info',
    ];
    const handleCloseAlert = () => {
        setErrorMsg({ title: '', message: '' });
        dispatch({ type: productActionType.hasError, payload: { value: false } });
    };

    useEffect(() => {
        if (productID && prodState.salePrice.value) {
            const currentPrice = String(prodState.salePrice.value).trim()
            if (!originalPriceRef.current) {
                originalPriceRef.current = currentPrice
                setOriginalPrice(currentPrice)
            }
        }
    }, [productID]);

    useEffect(() => {
        if (productID && prodState.salePrice.value && !originalPriceRef.current) {
            const currentPrice = String(prodState.salePrice.value).trim()
            originalPriceRef.current = currentPrice
            setOriginalPrice(currentPrice)
        }
    }, [prodState.salePrice.value]);

    const nextStep = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const isContinueDisabled = () => {
        if (currentStep === 0) {
            return !prodState.barcode.value || prodState.barcode.error;
        }
        if (currentStep === 1) {
            return !prodState.categoryID.value;
        }
        return false;
    };
    // "Save Draft" stays disabled until the user has actually entered something.
    // The add form is step-gated on the barcode (step 0 shows only the barcode),
    // so an empty barcode means a pristine form with nothing worth saving yet.
    const isSaveDraftDisabled = () => !prodState.barcode.value;
    const isSubmitDisabled = () => {
        return !(prodState.manufacturer_id.value && (!tenantTaxEnabled() || prodState.tax_class_id.value) &&
            prodState.model.value && prodState.productNameEn.value && prodState.productNameAr.value &&
            (((prodState.variations.length !== 0 && !prodState.quantity.value && !prodState.salePrice.value) ||
                (prodState.quantity.value && prodState.salePrice.value)
            )
            ))
    };

    const validateForm = (): ValidationResult => {
        dispatch({ type: productActionType.clearErrors, payload: { id: '', value: '' } });
        const result: ValidationResult = { missingFields: [], invalidValues: [], variationIssues: [] };
        let notValidateKeys = ['aboutEn', 'aboutAr', 'productDescriptionEn', 'productDescriptionAr', 'expiryDate', 'recommendedAge'];

        if (prodState.variations.length > 0) {
            notValidateKeys.push('salePrice', 'quantity', 'image');
        }

        // Map raw field keys to translated labels so validation errors are
        // localized (e.g. "recommendedAge" -> "العمر الموصى به") instead of
        // showing the raw English key to the user.
        const fieldLabels: Record<string, string> = {
            barcode: 'barcode', model: 'model_number', categoryID: 'category',
            productNameEn: 'prod_name_en', productNameAr: 'prod_name_ar',
            manufacturer_id: 'brand', recommendedAge: 'recommended_age',
            quantity: 'quantity', salePrice: 'sale_price', tax_class_id: 'tax_class',
            image: 'image',
        };
        const labelOf = (k: string) => (fieldLabels[k] ? formatMessage({ id: fieldLabels[k] }) : k);

        Object.keys(prodState).forEach((item) => {
            const key = prodState[item as keyof typeof prodState];

            if (key && typeof key === 'object') {
                if (key.hasOwnProperty('error')) {
                    if (!notValidateKeys.includes(item) &&
                        (key.value === '' || typeof key.value === 'undefined') ||
                        (item === 'image' && typeof key.value === 'string' && !notValidateKeys.includes('image') && key.value.includes('logos/img-placeholder.png'))) {
                        result.missingFields.push(labelOf(item));
                        dispatch({ type: productActionType.fieldRequired, payload: { id: item, value: true } });
                    }
                    if (prodState.variations.length === 0 && ((item === 'quantity' && key.value < 0) || (item === 'salePrice' && key.value <= 0))) {
                        result.invalidValues.push(formatMessage({ id: 'field_must_be_positive' }, { field: labelOf(item) }));
                        dispatch({ type: productActionType.fieldRequired, payload: { id: item, value: true } });
                    }
                }
            }

            if (item === 'variations') {
                let variationError = false;

                prodState.variations.forEach((variation, index) => {
                    const indexS = index + 1;
                    if (!variation.barcode) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'barcode' })}`);
                        variationError = true;
                    }
                    if (!variation.color_id) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'color' })}`);
                        variationError = true;
                    }
                    if (!variation.size_id) {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'size' })}`);
                        variationError = true;
                    }
                    if (!variation.quantity || variation.quantity <= '0' || variation.quantity === '') {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'quantity' })}`);
                        variationError = true;
                    }
                    if (!variation.price || variation.price <= '0' || variation.price === '') {
                        result.variationIssues.push(`${formatMessage({ id: 'variations' })} ${indexS} ${formatMessage({ id: 'price' })}`);
                        variationError = true;
                    }
                });
                if (prodState.variationImages.length !== prodState.variations.length) {
                    for (let i = prodState.variationImages.length; i < prodState.variations.length - prodState.variationImages.length; i++) {
                        result.variationIssues.push(`${formatMessage({ id: 'variation_missing_img' })} ${i + 1}`);
                    }
                    variationError = true;
                }

                if (variationError) {
                    setKey('variations');
                }
            }
        });

        return result;
    };
    const collectMessages = (response: any, depth: number = 0): string[] => {
        const messages: string[] = [];

        if (typeof response === 'object' && response !== null) {
            for (const key in response) {
                if (response.hasOwnProperty(key)) {
                    if (Array.isArray(response[key])) {
                        messages.push(`${''.repeat(depth * 2)}${key}:`);
                        response[key].forEach((item: any, index: number) => {
                            messages.push(`${''.repeat((depth + 1) * 2)}${index}:`);
                            messages.push(...collectMessages(item, depth + 2));
                        });
                    }
                    else if (typeof response[key] === 'object') {
                        messages.push(`${''.repeat(depth * 2)}${key}:`);
                        messages.push(...collectMessages(response[key], depth + 1));
                    }
                    else {
                        messages.push(`${''.repeat(depth * 2)}${key}: ${response[key]}`);
                    }
                }
            }
        } else {
            messages.push(response);
        }

        return messages;
    };

        const showAlert = (title: string, message: string) => {
            setErrorMsg({
                title,
                message,
            })
            dispatch({type: productActionType.hasError, payload: {value: true}})
            setIsSubmitting(false);
        }

    const performSave = async (specialPriceAction?: 'deactivate' | 'keep_active') => {
        let loadingStarted = false;
        try {
            const validation = validateForm();
            const populated = [
                validation.missingFields.length > 0,
                validation.invalidValues.length > 0,
                validation.variationIssues.length > 0,
            ].filter(Boolean).length;

            if (populated > 1) {
                throw new FormValidationError([
                    ...validation.missingFields,
                    ...validation.invalidValues,
                    ...validation.variationIssues,
                ]);
            }
            if (validation.missingFields.length > 0) {
                throw new MissingFieldsError(validation.missingFields);
            }
            if (validation.invalidValues.length > 0) {
                throw new InvalidFieldValuesError(validation.invalidValues);
            }
            if (validation.variationIssues.length > 0) {
                throw new VariationValidationError(validation.variationIssues);
            }

            dispatch({type: productActionType.isLoading})
            loadingStarted = true;
            setIsSubmitting(true);
            const resp:any = await addProduct(language, currentUser?.token, prodState, productID, specialPriceAction, isShopUser(currentUser));

            // Duplicate / already-submitted product: the backend returns HTTP 200
            // with { status: 'error', message } (ProductController@add). It's not a
            // real add, so surface the friendly message and stop here — otherwise
            // the code below treats it as success (clears the form + navigates to
            // /thank-you as if the product was created).
            if (resp?.data?.status === 'error' && resp?.data?.message) {
                showAlert(
                    formatMessage({id: 'product_notice_title'}),
                    resp.data.message
                );
                return false;
            }

            const messages = collectMessages(resp);
            const seenMessages = new Set<string>();
            const pushMessage = (msg: unknown) => {
                if (typeof msg === 'string' && msg && !seenMessages.has(msg)) {
                    seenMessages.add(msg);
                }
            };

            if (resp && resp.errors) {
                Object.values(resp.errors).forEach((group: any) => {
                    if (group && typeof group === 'object') {
                        Object.values(group).forEach((message: any) => {
                            if (typeof message === 'string') {
                                pushMessage(message);
                            } else if (message && typeof message === 'object') {
                                pushMessage(Object.values(message)[0]);
                            }
                        });
                    } else if (typeof group === 'string') {
                        pushMessage(group);
                    }
                });
            }
            const formatedErrorMessageFinal = Array.from(seenMessages);

            if (!resp || resp.errors) {
                if (resp && resp.errors) {
                    const payload = {errors:resp.errors}
                    dispatch({type: productActionType.validationErrors,payload});
                    showAlert(formatMessage({id: 'something_wrong_title_add_prod'}),
                        formatedErrorMessageFinal.length > 0
                            ? formatedErrorMessageFinal.join('\n')
                            : messages[messages.length - 1])
                    return false;
                } else {
                    throw new Error('')
                }
            } else {
                // Clear the image after successful submission
                prodDispatch({type: productActionType.image, payload: {id: 'image', value: toAbsoluteUrl('/media/logos/img-placeholder.png')}});
                localStorage.setItem('thankYouPageURL', window.location.href);
                navigate(`/thank-you?go=request-${productID ? 'update' : 'add'}-products&message=${encodeURIComponent(resp.data.message)}`);
            }
        } catch (e) {
            if (e instanceof MissingFieldsError) {
                showAlert(
                    formatMessage({ id: 'missing_required_fields_title' }),
                    `${formatMessage({ id: 'missing_required_fields_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof InvalidFieldValuesError) {
                showAlert(
                    formatMessage({ id: 'invalid_field_values_title' }),
                    `${formatMessage({ id: 'invalid_field_values_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof VariationValidationError) {
                showAlert(
                    formatMessage({ id: 'variation_validation_title' }),
                    `${formatMessage({ id: 'variation_validation_message' })}: ${e.details.join(', ')}`
                );
            } else if (e instanceof FormValidationError) {
                showAlert(
                    formatMessage({ id: 'form_validation_failed_title' }),
                    `${formatMessage({ id: 'form_validation_failed_message' })}: ${e.details.join(', ')}`
                );
            } else {
                showAlert(
                    formatMessage({ id: 'something_wrong_title_add_prod' }),
                    e instanceof Error && e.message
                        ? e.message
                        : formatMessage({ id: 'something_wrong_desc_add_prod' })
                );
            }
        } finally {
            if (loadingStarted) {
                dispatch({type: productActionType.isLoading})
            }
            setIsSubmitting(false)
        }
    }

    const saveProduct = async () => {
        if (productID && originalPriceRef.current) {
            const currentPrice = String(prodState.salePrice.value || '').trim()
            const originalPrice = String(originalPriceRef.current || '').trim()
            
            const currentPriceNum = parseFloat(currentPrice) || 0
            const originalPriceNum = parseFloat(originalPrice) || 0
            const priceChanged = Math.abs(currentPriceNum - originalPriceNum) > 0.01
            
            if (priceChanged) {
                const currentSpecialPrice = prodState.current_special_price

                if (currentSpecialPrice != null) {
                    const currentPriceNum = parseFloat(currentPrice) || 0
                    const specialPriceStr = String(currentSpecialPrice)

                    if (currentPriceNum < currentSpecialPrice) {
                        showAlert(
                            formatMessage({ id: 'price_below_special_title' }),
                            formatMessage(
                                { id: 'price_below_special_message' },
                                {
                                    newPrice: currentPrice,
                                    specialPrice: specialPriceStr,
                                }
                            )
                        )
                        return
                    }

                    setShowSpecialPriceModal(true)
                    return
                }
            }
        }

        await performSave()
    }

    const handleDeactivateSpecialPrice = async () => {
        setSpecialPriceDecision('deactivate')
        setShowSpecialPriceModal(false)
        await performSave('deactivate')
        setSpecialPriceDecision(null)
    }

    const handleKeepSpecialPriceActive = () => {
        setSpecialPriceDecision('keep_active')
        setShowSpecialPriceModal(false)

        const specialPriceNum = Number(prodState.current_special_price ?? 0) || 0
        const originalPriceNum = parseFloat(originalPriceRef.current || '0') || 0
        const currentPriceNum = parseFloat(prodState.salePrice.value || '0') || 0
        
        if (originalPriceNum > 0 && specialPriceNum > 0) {
            const discountPercentage = ((originalPriceNum - specialPriceNum) / originalPriceNum) * 100
            const newSpecialPriceNum = currentPriceNum * (1 - discountPercentage / 100)
            const newSpecialPrice = newSpecialPriceNum.toFixed(4)
            setCalculatedNewSpecialPrice(newSpecialPrice)
            setShowNewSpecialPriceModal(true)
        } else {
            performSave('keep_active')
        }
    }

    const handleConfirmNewSpecialPrice = async () => {
        setShowNewSpecialPriceModal(false)
        await performSave('keep_active')
        setSpecialPriceDecision(null)
    }

    const handleCancelNewSpecialPrice = () => {
        setShowNewSpecialPriceModal(false)
        setSpecialPriceDecision(null)
        setIsSubmitting(false)
    }

    const handleCloseSpecialPriceModal = () => {
        setShowSpecialPriceModal(false)
        setIsSubmitting(false)
    }

    const saveDraft = async () => {
        // The draft path skips validateForm, so guard negative stock here too —
        // a negative supplier_quantity must never be persisted, even as a draft.
        const singleQty = Number(prodState.quantity?.value);
        const hasNegativeQty =
            (prodState.variations.length === 0 && prodState.quantity?.value !== '' && !isNaN(singleQty) && singleQty < 0) ||
            prodState.variations.some((v: any) => Number(v.quantity) < 0);
        if (hasNegativeQty) {
            showAlert(
                formatMessage({ id: 'image_error_fields' }),
                formatMessage({ id: 'field_must_be_positive' }, { field: formatMessage({ id: 'quantity' }) })
            );
            return;
        }
        setIsSubmitting(true);
        try {
            const data = await saveProductDraft(language, currentUser?.token, prodState, productID, isShopUser(currentUser));
            if (data && 'errors' in data) {
                showAlert(formatMessage({ id: 'image_error_fields' }), data.errors);
            } else {
                localStorage.setItem('thankYouPageURL', window.location.href);
                navigate(`/products/drafted-list`);
            }
        } catch (e: unknown) {
            if (e instanceof Error) {
                showAlert(formatMessage({ id: 'image_error_fields' }), e.message);
            } else {
                showAlert(formatMessage({ id: 'image_error_fields' }), 'An unknown error occurred.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <div>
                <ProductGeneralForm
                    prodState={prodState}
                    prodDispatch={prodDispatch}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    key2={key}
                    isSubmitDisabled={isSubmitDisabled()}
                />
                {/* "Create package" — only for a brand-new product, once a
                    barcode is entered. The package is built from the supplier's
                    own products; the backend derives its price. Never for a
                    partner shop's account: a package's availability comes from
                    national stock, which a shop's shelf is not part of (the
                    server refuses it with package_not_for_shop). */}
                {!productID && prodState?.barcode?.value && !isShopUser(currentUser) && (
                    <PackageSection
                        state={prodState}
                        dispatch={prodDispatch}
                        token={currentUser?.token}
                        language={language}
                    />
                )}
                <div className='aps-actionbar'>
                        <div className='aps-actionbar-right'>
                            <button
                                type="button"
                                className="aps-save-btn aps-save-btn--draft"
                                onClick={saveDraft}
                                disabled={isSaveDraftDisabled() || isSubmitting}
                            >
                                {formatMessage({id: 'save_draft'})}
                            </button>
                            {(currentStep < steps.length - 1 ) && (
                                <button
                                    type="button"
                                    className="aps-save-btn"
                                    onClick={nextStep}
                                    disabled={isContinueDisabled()}
                                >
                                    {formatMessage({id: 'continue'})}
                                </button>
                            )}
                            {!isSubmitDisabled() && (
                                <button
                                    type="submit"
                                    className="aps-save-btn"
                                    onClick={saveProduct}
                                    disabled={isSubmitDisabled() || isSubmitting}
                                >
                                    {formatMessage({id: 'save_changes'}) || formatMessage({id: 'submit'})}
                                </button>
                            )}
                        </div>
                    </div>
            </div>
            {errorMsg.title && errorMsg.message && (
                <AlertMessage
                    title={errorMsg.title}
                    message={errorMsg.message}
                    onClose={handleCloseAlert}
                />
            )}

                {showSpecialPriceModal && (
                <SpecialPriceDecisionModal
                    show={showSpecialPriceModal}
                    onClose={handleCloseSpecialPriceModal}
                    onDeactivate={handleDeactivateSpecialPrice}
                    onKeepActive={handleKeepSpecialPriceActive}
                    oldPrice={originalPriceRef.current || prodState.salePrice.value}
                    newPrice={prodState.salePrice.value}
                    specialPrice={String(prodState.current_special_price ?? '')}
                />
            )}

            {showNewSpecialPriceModal && (
                <NewSpecialPriceModal
                    show={showNewSpecialPriceModal}
                    onConfirm={handleConfirmNewSpecialPrice}
                    onCancel={handleCancelNewSpecialPrice}
                    newSpecialPrice={calculatedNewSpecialPrice}
                />
            )}

        </>
    );
};

export default ProductGeneralTab
